/**
 * v-safe 指令
 * 用于处理 HTML 内容中的图片 URL，解决混合内容问题
 * 
 * 功能：
 * 1. 将外部图片链接转换为代理路径或当前协议
 * 2. 解决 HTTPS 页面加载 HTTP 图片的问题
 */

/**
 * 处理 HTML 内容中的图片 URL
 * @param {String} html - 原始 HTML 内容
 * @param {Object} options - 配置选项
 * @returns {String} 处理后的 HTML
 */
export function processImageUrls(html, options = {}) {
  if (!html || typeof html !== 'string') return html;
  
  const {
    useProxy = true // 是否使用代理路径
  } = options;
  
  // 更健壮的正则：匹配 img 标签中的 src 属性（支持单引号/双引号，属性顺序任意）
  return html.replace(/<img\b([^>]*?)\bsrc\s*=\s*["']([^"']+)["']([^>]*)>/gi, (match, before, src, after) => {
    let newSrc = convertImageUrl(src, { useProxy });
    return `<img${before}src="${newSrc}"${after}>`;
  });
}

/**
 * 转换单个图片 URL
 * @param {String} src - 原始图片 URL
 * @param {Object} options - 配置选项
 * @returns {String} 转换后的 URL
 */
export function convertImageUrl(src, options = {}) {
  const { useProxy = true } = options;
  
  if (!src) return src;
  
  const currentProtocol = window.location.protocol;
  
  // ⭐ 只在 HTTPS 环境下才需要转换图片 URL
  if (currentProtocol !== 'https:') {
    // HTTP 环境：保持原始 URL 不变
    return src;
  }
  
  // HTTPS 环境：处理混合内容问题
  
  // 处理协议相对 URL（//开头）
  if (src.startsWith('//')) {
    // 补全协议
    src = window.location.protocol + src;
  }
  
  // 处理 wancode.net 域名的图片
  if (src.includes('wancode.net')) {
    try {
      const url = new URL(src);
      
      // 端口 9002 的图片（/exam/ 路径）
      if (url.port === '9002' && url.pathname.startsWith('/exam/')) {
        if (useProxy) {
          const imagePath = url.pathname.replace('/exam/', '');
          return `/exam-images/${imagePath}${url.search}`;
        }
      }
      
      // 端口 8585 的图片（/wancode/back/image/read/ 路径）
      if (url.port === '8585' && url.pathname.includes('/image/read/')) {
        if (useProxy) {
          // 提取 image/read/ 后面的 ID
          const match = url.pathname.match(/\/image\/read\/(.+)/);
          if (match) {
            const imageId = match[1];
            return `/back-images/${imageId}${url.search}`;
          }
        }
      }
      
      // 其他端口的 wancode.net 图片，转换为协议相对 URL
      if (src.startsWith('http://')) {
        return src.replace(/^https?:/, '');
      }
      
    } catch (e) {
      console.warn('Invalid URL:', src);
    }
  }
  
  // 处理其他 HTTP 图片（HTTPS 环境下）
  if (src.startsWith('http://')) {
    return src.replace(/^https?:/, '');
  }
  
  return src;
}

/**
 * Vue 自定义指令
 */
const SafeDirective = {
  inserted(el, binding) {
    const options = binding.value || {};
    processElementImages(el, options);
  },
  update(el, binding) {
    const options = binding.value || {};
    processElementImages(el, options);
  }
};

/**
 * 处理元素中的图片
 */
function processElementImages(el, options = {}) {
  // 处理 img 标签
  const images = el.querySelectorAll('img');
  images.forEach(img => {
    const src = img.getAttribute('src');
    if (src) {
      const newSrc = convertImageUrl(src, options);
      if (newSrc !== src) {
        img.setAttribute('src', newSrc);
        // 添加错误处理
        if (!img.hasAttribute('data-error-handled')) {
          img.setAttribute('data-error-handled', 'true');
          img.addEventListener('error', function handleError() {
            console.warn('Image load failed:', this.src);
            // 如果代理加载失败，尝试原始 URL
            if (this.src.startsWith('/exam-images/')) {
              const originalPath = this.src.replace('/exam-images/', '');
              this.src = `http://www.wancode.net:9002/exam/${originalPath}`;
            } else if (this.src.startsWith('/back-images/')) {
              const imageId = this.src.replace('/back-images/', '').split('?')[0];
              this.src = `http://www.wancode.net:8585/wancode/back/image/read/${imageId}`;
            }
          }, { once: true });
        }
      }
    }
  });
}

export default SafeDirective;
